/**
 * MiniProjectCompletionDialog — the Mark-Complete experience (DEV-1265, U1b).
 *
 * Two phases in one dialog:
 *   1. CONFIRM   — "Here's how it landed" outcome preview + Complete / Cancel.
 *                  Running the transition here surfaces the goal guardrail
 *                  ("required goals below target") inline instead of a raw
 *                  toast. Replaces the DEV-1271 window.confirm stopgap.
 *   2. CELEBRATE — post-completion next steps. "Write the retrospective"
 *                  deep-links to the About-tab retrospective section (U1a).
 *                  "Create a Playbook" lands in U2 (the stepper) — shown as
 *                  a forthcoming step until then.
 *
 * Outcome tiles use the Management-tab status palette per CLAUDE.md
 * (blue=done, green=on-track, yellow=attention) — never red for
 * "incomplete".
 *
 * Mockup: design-mockups/dev-903-completion-playbook-flow.html (Screen 1).
 */

import { useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  transitionMiniProject,
  type MiniProject,
} from "../../api/miniProjects";
import { useApiMiniProjectSubEntities } from "../../api/useApiMiniProjectSubEntities";
import { ApiError } from "../../api/client";
import styles from "./MiniProjectCompletionDialog.module.css";

interface Props {
  miniProject: MiniProject;
  /** Whether the playbook path is available to this user (Lead or
   *  owning-community admin). The card is hidden when false. */
  canCreatePlaybook?: boolean;
  onClose: () => void;
  /** Fired after a successful completion so the page can refetch. */
  onCompleted: () => void;
  /** Navigate to the About tab's retrospective section (U1a). */
  onWriteRetrospective: () => void;
  /** Open the playbook creation flow (U2). Undefined until U2 ships. */
  onCreatePlaybook?: () => void;
}

export default function MiniProjectCompletionDialog({
  miniProject,
  canCreatePlaybook = false,
  onClose,
  onCompleted,
  onWriteRetrospective,
  onCreatePlaybook,
}: Props) {
  const { t } = useAppTranslation("pages");
  const { goals, tasks } = useApiMiniProjectSubEntities(miniProject.miniProjectGuid);

  // A project re-opening this dialog while already complete jumps to the
  // celebrate phase (re-entry path).
  const [phase, setPhase] = useState<"confirm" | "celebrate">(
    miniProject.status === "complete" ? "celebrate" : "confirm",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── Outcome tiles ────────────────────────────────────────────── */
  const tiles = useMemo(() => {
    const totalGoals = goals.length;
    const goalsAtTarget = goals.filter(
      (g) => g.targetValue != null && g.currentValue >= g.targetValue,
    ).length;
    const totalTasks = tasks.length;
    const tasksDone = tasks.filter((tk) => tk.status === "done").length;

    // Management palette: blue when all met, green while in-progress-good,
    // yellow when short. Never red (per CLAUDE.md status semantics).
    const goalTone =
      totalGoals === 0 ? "muted" : goalsAtTarget >= totalGoals ? "done" : "warn";
    const taskTone =
      totalTasks === 0 ? "muted" : tasksDone >= totalTasks ? "done" : "warn";

    return {
      goals: { value: `${goalsAtTarget} / ${totalGoals}`, tone: goalTone },
      tasks: { value: `${tasksDone} / ${totalTasks}`, tone: taskTone },
      budget:
        miniProject.plannedBudgetAmount != null
          ? {
              value: `${miniProject.plannedBudgetCurrency || ""} ${miniProject.plannedBudgetAmount}`.trim(),
              tone: "muted" as const,
            }
          : null,
    };
  }, [goals, tasks, miniProject.plannedBudgetAmount, miniProject.plannedBudgetCurrency]);

  /* ── Complete action ──────────────────────────────────────────── */
  const handleComplete = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await transitionMiniProject(miniProject.miniProjectGuid, { newStatus: "complete" });
      onCompleted();
      setPhase("celebrate");
    } catch (e) {
      // The backend goal guardrail returns a clear message — surface it.
      const msg =
        e instanceof ApiError
          ? (e.body as { error?: string })?.error || e.message
          : e instanceof Error
            ? e.message
            : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, [miniProject.miniProjectGuid, onCompleted]);

  const toneClass = (tone: string) =>
    tone === "done" ? styles.tileDone : tone === "warn" ? styles.tileWarn : styles.tileMuted;

  const dialog = (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* ── Header ── */}
        <div className={styles.head}>
          <h2 className={styles.title}>
            {phase === "celebrate"
              ? t("miniProject.completion.celebrateTitle", { name: miniProject.title })
              : t("miniProject.completion.confirmTitle", { name: miniProject.title })}
          </h2>
          <button className={styles.x} onClick={onClose} aria-label={t("miniProject.completion.close")}>
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {phase === "celebrate" && (
            <div className={styles.celebrate}>
              <span className={styles.celebrateIcon} aria-hidden="true">🎉</span>
              <p>{t("miniProject.completion.celebrateLead")}</p>
            </div>
          )}
          {phase === "confirm" && (
            <p className={styles.intro}>{t("miniProject.completion.confirmLead")}</p>
          )}

          {/* ── Outcome tiles ── */}
          <div className={styles.tiles}>
            <div className={`${styles.tile} ${toneClass(tiles.goals.tone)}`}>
              <div className={styles.tileLabel}>{t("miniProject.completion.tileGoals")}</div>
              <div className={styles.tileValue}>{tiles.goals.value}</div>
            </div>
            <div className={`${styles.tile} ${toneClass(tiles.tasks.tone)}`}>
              <div className={styles.tileLabel}>{t("miniProject.completion.tileTasks")}</div>
              <div className={styles.tileValue}>{tiles.tasks.value}</div>
            </div>
            {tiles.budget && (
              <div className={`${styles.tile} ${toneClass(tiles.budget.tone)}`}>
                <div className={styles.tileLabel}>{t("miniProject.completion.tileBudget")}</div>
                <div className={styles.tileValue}>{tiles.budget.value}</div>
              </div>
            )}
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {/* ── Celebrate: next steps ── */}
          {phase === "celebrate" && (
            <div className={styles.nextSteps}>
              <button className={styles.nextStep} onClick={onWriteRetrospective}>
                <span className={styles.nextIcon}>📝</span>
                <span className={styles.nextText}>
                  <strong>{t("miniProject.completion.retroTitle")}</strong>
                  <span>{t("miniProject.completion.retroHint")}</span>
                </span>
              </button>

              {canCreatePlaybook && (
                <button
                  className={`${styles.nextStep} ${styles.nextStepPlaybook}`}
                  onClick={onCreatePlaybook}
                  disabled={!onCreatePlaybook}
                  title={!onCreatePlaybook ? t("miniProject.completion.playbookSoon") : undefined}
                >
                  <span className={styles.nextIcon}>🌱</span>
                  <span className={styles.nextText}>
                    <strong>{t("miniProject.completion.playbookTitle")}</strong>
                    <span>
                      {onCreatePlaybook
                        ? t("miniProject.completion.playbookHint")
                        : t("miniProject.completion.playbookSoon")}
                    </span>
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={styles.foot}>
          {phase === "confirm" ? (
            <>
              <span className={styles.footNote}>{t("miniProject.completion.reopenNote")}</span>
              <span className={styles.spacer} />
              <button className={styles.btnGhost} onClick={onClose} disabled={busy}>
                {t("miniProject.completion.cancel")}
              </button>
              <button className={styles.btnPrimary} onClick={handleComplete} disabled={busy}>
                {busy ? t("miniProject.completion.completing") : t("miniProject.completion.complete")}
              </button>
            </>
          ) : (
            <>
              <span className={styles.spacer} />
              <button className={styles.btnGhost} onClick={onClose}>
                {t("miniProject.completion.done")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
}
